import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { store, persistor } from '../store';
import { syncLocationData } from '../store/actions/location';
import { fetchProjects } from '../store/actions/projects';
import { LOCATION_SHARING_OPT_IN_KEY } from '../constants/location';
import debug from './debug';

export const LOCATION_BACKGROUND_TASK = 'ELEPLAN_LOCATION_UPDATES';

/** Bump when `startLocationUpdatesAsync` options change so existing installs re-apply policy. */
const LOCATION_BG_OPTIONS_VERSION = 4;
const LOCATION_BG_OPTIONS_VERSION_KEY = '@eleplan/location_bg_options_version';

/**
 * Caregiver location sharing: opportunistic, low-frequency updates — not continuous GPS.
 * Previously used Accuracy.Low (cell-tower only, 100–500m drift) which was too imprecise
 * for the 150m Nearby Search radius used by location visit follow-ups.
 * Accuracy.Balanced (WiFi-assisted, 30–100m) gives reliable place identification
 * without requiring continuous GPS.
 *
 * A stopped phone reports nothing, so the server places a visit at the last
 * point before the stop. With a 500 m filter that point could be anywhere up to
 * 500 m short of the building (250 m on average), wider than the 150 m place
 * lookup. 200 m keeps it close. The filter only decides which fixes the app is
 * handed (accuracy decides how the radio is used), and uploads stay batched at
 * 15 min / 500 m by the deferral below. The server's departure threshold
 * (location-dwell.js) is 200 m to match.
 */
const BG_LOCATION = {
  /** Minimum movement before a new point (meters) */
  distanceIntervalM: 200,
  /** Android min gap between updates (ms) */
  timeIntervalMs: 15 * 60 * 1000,
  /** Batch / defer deliveries on iOS (meters + ms) */
  deferredDistanceM: 500,
  deferredIntervalMs: 15 * 60 * 1000,
} as const;

/** Safety: even if the OS delivers bursts, avoid syncing to the API more than once per interval. */
const MIN_MS_BETWEEN_SERVER_SYNCS = 10 * 60 * 1000;
const LOCATION_BG_LAST_SYNC_KEY = '@eleplan/location_bg_last_server_sync_ms';

/**
 * Points waiting to be uploaded. A wake that cannot upload (throttled, the
 * persisted session not loaded yet, offline) used to return and drop its
 * batch — and with it the point where a visit started. They wait here instead
 * and go up, oldest first, with the next batch.
 */
const LOCATION_BG_PENDING_KEY = '@eleplan/location_bg_pending_points';
const MAX_PENDING_POINTS = 200;
const MAX_PENDING_AGE_MS = 48 * 60 * 60 * 1000;
/** A cold background launch reads the session from disk; don't wait on it forever. */
const REHYDRATION_TIMEOUT_MS = 5000;

type LocationPoint = {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  altitude: number | null;
  heading: number | null;
  speed: number | null;
  timestamp: string;
  source: string;
};

let TaskManager: typeof import('expo-task-manager') | null = null;
let Location: typeof import('expo-location') | null = null;

if (Platform.OS !== 'web') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    TaskManager = require('expo-task-manager') as typeof import('expo-task-manager');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    Location = require('expo-location') as typeof import('expo-location');
  } catch {
    // Native module not available in this runtime.
  }
}

/**
 * When iOS relaunches a terminated app for a location event, the task can run
 * before redux-persist has read the session back from disk, so the account and
 * user ids it needs are still empty. Wait for that read to finish.
 */
function waitForRehydration(): Promise<void> {
  if (!persistor || persistor.getState().bootstrapped) return Promise.resolve();
  return new Promise((resolve) => {
    let unsubscribe: () => void = () => {};
    const done = () => {
      clearTimeout(timer);
      unsubscribe();
      resolve();
    };
    const timer = setTimeout(done, REHYDRATION_TIMEOUT_MS);
    unsubscribe = persistor.subscribe(() => {
      if (persistor.getState().bootstrapped) done();
    });
    if (persistor.getState().bootstrapped) done();
  });
}

function toPoint(update: any): LocationPoint {
  const coords = update.coords;
  return {
    latitude: coords.latitude,
    longitude: coords.longitude,
    accuracy: typeof coords.accuracy === 'number' ? coords.accuracy : null,
    altitude: typeof coords.altitude === 'number' ? coords.altitude : null,
    heading: typeof coords.heading === 'number' ? coords.heading : null,
    speed: typeof coords.speed === 'number' ? coords.speed : null,
    timestamp: new Date(update.timestamp || Date.now()).toISOString(),
    source: Platform.OS === 'ios' ? 'ios-location-background' : 'android-location-background',
  };
}

async function readPending(): Promise<LocationPoint[]> {
  try {
    const raw = await AsyncStorage.getItem(LOCATION_BG_PENDING_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

async function writePending(points: LocationPoint[]): Promise<void> {
  if (points.length === 0) {
    await AsyncStorage.removeItem(LOCATION_BG_PENDING_KEY);
    return;
  }
  await AsyncStorage.setItem(LOCATION_BG_PENDING_KEY, JSON.stringify(points));
}

/** Oldest first, one entry per timestamp, nothing stale, capped at the newest MAX_PENDING_POINTS. */
function mergePending(pending: LocationPoint[], incoming: LocationPoint[], nowMs: number): LocationPoint[] {
  const byTimestamp = new Map<string, LocationPoint>();
  for (const point of [...pending, ...incoming]) {
    const at = Date.parse(point?.timestamp);
    if (!Number.isFinite(at) || nowMs - at > MAX_PENDING_AGE_MS) continue;
    byTimestamp.set(point.timestamp, point);
  }
  return Array.from(byTimestamp.values())
    .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp))
    .slice(-MAX_PENDING_POINTS);
}

async function getProjectIds(): Promise<string[]> {
  const state: any = store.getState();
  const accountId = state?.session?.account?.account_id;
  const cognitoId = state?.authentication?.user?.cognito_id;
  if (!accountId || !cognitoId) return [];

  let projectRows: any[] = [];
  const fetchRes: any = await store.dispatch(fetchProjects(accountId, cognitoId) as any).catch(() => null);
  if (Array.isArray(fetchRes?.payload)) {
    projectRows = fetchRes.payload;
  }
  if (projectRows.length === 0) {
    projectRows = Object.values(state?.projects?.list ?? {});
  }

  return Array.from(
    new Set(
      projectRows
        .map((p: any) => p?.id ?? p?.project_id)
        .filter((id: any) => id != null)
        .map((id: any) => String(id)),
    ),
  );
}

if (TaskManager && Location) {
  try {
    TaskManager.defineTask(LOCATION_BACKGROUND_TASK, async ({ data, error }) => {
      try {
        if (error) {
          debug.warn('Location background task error', error);
          return;
        }

        const optedIn = await AsyncStorage.getItem(LOCATION_SHARING_OPT_IN_KEY);
        if (optedIn !== 'true') return;

        const updates = (data as any)?.locations as Array<any> | undefined;
        const incoming = Array.isArray(updates) ? updates.filter((u) => u?.coords).map(toPoint) : [];
        if (incoming.length === 0) return;

        const nowMs = Date.now();
        const points = mergePending(await readPending(), incoming, nowMs);

        const lastSyncStr = await AsyncStorage.getItem(LOCATION_BG_LAST_SYNC_KEY);
        const lastSync = lastSyncStr ? parseInt(lastSyncStr, 10) : 0;
        if (lastSync && nowMs - lastSync < MIN_MS_BETWEEN_SERVER_SYNCS) {
          await writePending(points);
          return;
        }

        await waitForRehydration();
        const projectIds = await getProjectIds();
        if (projectIds.length === 0) {
          await writePending(points);
          return;
        }

        // Upload every point, not just the newest — server-side dwell
        // detection needs the intermediate points to catch a short visit that
        // starts and ends between two background deliveries. /location/sync
        // takes one point per request (no bulk endpoint), and its DynamoDB
        // write is a non-atomic read-modify-write per member, so points for
        // the same project post one at a time, in order, rather than in
        // parallel — a concurrent pair could race and drop one. The 10-minute
        // floor above still throttles at the batch level, not per point, so a
        // multi-point batch counts as a single sync. A point no plan accepted
        // (offline, say) stops the run; it and everything after it wait for
        // the next wake, so the server still sees them in order.
        let sent = 0;
        for (const point of points) {
          const results = await Promise.allSettled(
            projectIds.map((pid) => store.dispatch(syncLocationData(pid, point) as any)),
          );
          const accepted = results.some((r) => r.status === 'fulfilled' && (r.value as any)?.success === true);
          if (!accepted) break;
          sent += 1;
        }
        await writePending(points.slice(sent));
        if (sent > 0) await AsyncStorage.setItem(LOCATION_BG_LAST_SYNC_KEY, String(nowMs));
      } catch (err) {
        debug.warn('Location background task failed', err);
      }
    });
  } catch {
    // Task already defined or task manager unavailable in this runtime.
  }
}

export async function registerLocationBackgroundUpdates(): Promise<void> {
  if (Platform.OS === 'web' || !Location || !TaskManager) return;
  try {
    const optedIn = await AsyncStorage.getItem(LOCATION_SHARING_OPT_IN_KEY);
    if (optedIn !== 'true') return;

    const fg = await Location.getForegroundPermissionsAsync();
    const bg = await Location.getBackgroundPermissionsAsync();
    if (fg.status !== 'granted' || bg.status !== 'granted') return;

    const appliedVersion = await AsyncStorage.getItem(LOCATION_BG_OPTIONS_VERSION_KEY);
    const started = await Location.hasStartedLocationUpdatesAsync(LOCATION_BACKGROUND_TASK);
    if (started && appliedVersion === String(LOCATION_BG_OPTIONS_VERSION)) {
      return;
    }
    if (started) {
      await Location.stopLocationUpdatesAsync(LOCATION_BACKGROUND_TASK);
    }

    await Location.startLocationUpdatesAsync(LOCATION_BACKGROUND_TASK, {
      accuracy: Location.Accuracy.Balanced,
      timeInterval: BG_LOCATION.timeIntervalMs,
      distanceInterval: BG_LOCATION.distanceIntervalM,
      deferredUpdatesDistance: BG_LOCATION.deferredDistanceM,
      deferredUpdatesInterval: BG_LOCATION.deferredIntervalMs,
      pausesUpdatesAutomatically: true,
      activityType: Location.ActivityType.Other,
      showsBackgroundLocationIndicator: false,
      foregroundService: Platform.OS === 'android'
        ? {
            notificationTitle: 'Location sharing',
            notificationBody: 'Occasional location updates for your care team.',
            notificationColor: '#2563EB',
          }
        : undefined,
    });
    await AsyncStorage.setItem(LOCATION_BG_OPTIONS_VERSION_KEY, String(LOCATION_BG_OPTIONS_VERSION));
    debug.log('ui', 'Location background updates registered (low-frequency policy)');
  } catch (err) {
    debug.warn('Failed to register location background updates', err);
  }
}

export async function unregisterLocationBackgroundUpdates(): Promise<void> {
  if (Platform.OS === 'web' || !Location) return;
  try {
    const started = await Location.hasStartedLocationUpdatesAsync(LOCATION_BACKGROUND_TASK);
    if (!started) return;
    await Location.stopLocationUpdatesAsync(LOCATION_BACKGROUND_TASK);
    await AsyncStorage.removeItem(LOCATION_BG_OPTIONS_VERSION_KEY);
    debug.log('ui', 'Location background updates unregistered');
  } catch (err) {
    debug.warn('Failed to unregister location background updates', err);
  }
}

/**
 * Report one location point right now, for a single plan.
 *
 * The background task reports on MOVEMENT (500m) or a 15-minute floor,
 * whichever comes first — which is the right policy for a passive signal and
 * the wrong one for a caregiver who just asked. A stationary phone can
 * legitimately go many hours without emitting a point, and the wellness rollup
 * only calls location `source_offline` after 24 hours, so "Out & about" spent
 * that whole window saying "home so far" on evidence that had gone quiet.
 *
 * This is the on-demand counterpart. It runs on the RECIPIENT's device, woken
 * by the same `wellness_check_now` push that already triggers a health sync —
 * never on the caregiver's, which is the whole point: reading position on the
 * viewing device would file the caregiver's whereabouts as the recipient's.
 *
 * Deliberately quiet about failure. The caller is a background wake with no UI
 * attached, and a permission the user hasn't granted is a normal state, not an
 * error worth surfacing.
 *
 * @returns true when a point was actually reported.
 */
export async function reportLocationNow(projectId: number | string): Promise<boolean> {
  if (Platform.OS === 'web' || !Location) return false;
  try {
    // Same opt-in gate the background task honours. Someone who turned
    // location sharing off must not be locatable by a check-now.
    const optedIn = await AsyncStorage.getItem(LOCATION_SHARING_OPT_IN_KEY);
    if (optedIn !== 'true') return false;

    // Foreground permission is enough for a one-shot read while the app is
    // awake — this deliberately does NOT require the Always grant the
    // background task needs, so a "While Using" install still answers.
    const { status } = await Location.getForegroundPermissionsAsync();
    if (status !== 'granted') return false;

    const position = await Location.getCurrentPositionAsync({
      accuracy: Location.Accuracy.Balanced,
    });
    const coords = position?.coords;
    if (!coords) return false;

    await store.dispatch(syncLocationData(projectId, {
      latitude: coords.latitude,
      longitude: coords.longitude,
      accuracy: typeof coords.accuracy === 'number' ? coords.accuracy : null,
      altitude: typeof coords.altitude === 'number' ? coords.altitude : null,
      heading: typeof coords.heading === 'number' ? coords.heading : null,
      speed: typeof coords.speed === 'number' ? coords.speed : null,
      timestamp: new Date(position.timestamp || Date.now()).toISOString(),
      // Distinct from the background sources so the origin of a point stays
      // legible downstream — this one was asked for, not observed.
      source: Platform.OS === 'ios' ? 'ios-location-on-demand' : 'android-location-on-demand',
    }) as any);
    return true;
  } catch (err) {
    debug.warn('On-demand location report failed', err);
    return false;
  }
}
